import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Select, Spin } from 'antd';
import type { SelectProps } from 'antd';
import { useSelect } from '@refinedev/antd';
import { useOne } from '@refinedev/core';
import { buildPatientSearchFilters } from '@src/utils/patientSearchFilters';

export interface SearchSelectFieldProps
  extends Omit<SelectProps<any>, 'options' | 'children'> {
  resource: string;
  optionValue: string;
  searchFields: string[];
  searchDebounceMs: number;
  filters: any[];
  sorters: any[];
  pagination: {
    pageSize: number;
  };
  queryOptions: {
    enabled: boolean;
    staleTime: number;
    cacheTime: number;
    refetchOnWindowFocus: boolean;
    refetchOnMount: boolean;
  };
  initialValue: any;
  optionRender: (option: any) => React.ReactNode;
  required?: boolean;
  status?: 'error' | 'warning';
  autoSelectFirst?: boolean; // New prop to enable auto-selecting first option
}

const DEFAULT_QUERY_OPTIONS = {
  enabled: true,
  staleTime: 0,
  cacheTime: 0,
  refetchOnWindowFocus: true,
  refetchOnMount: true,
};

const DEFAULT_PAGINATION = { pageSize: 10 };

function SearchSelectField({
  resource,
  optionValue = 'id',
  searchFields,
  searchDebounceMs = 300,
  filters = [],
  sorters = [],
  pagination = DEFAULT_PAGINATION,
  queryOptions = DEFAULT_QUERY_OPTIONS,
  value,
  onChange,
  initialValue,
  optionRender,
  required = false,
  status,
  autoSelectFirst = false,
  ...props
}: SearchSelectFieldProps) {
  // State management
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [internalValue, setInternalValue] = useState<any>(null);
  const isInitialLoad = useRef(true);
  const isUserSelection = useRef(false);

  // Initial value handling - extract ID from object if needed
  const extractId = useCallback((val: any) => {
    if (!val) return null;
    // If it's an object with value property (SearchSelect format), extract the value
    if (typeof val === 'object' && val.hasOwnProperty('value')) {
      return val.value;
    }
    // If it's a primitive value (string/number), use it directly
    return val;
  }, []);
  
  const initialValueId = extractId(initialValue || value);

  // Set initial internalValue if we have an initialValue
  useEffect(() => {
    if (initialValue && !internalValue) {
      setInternalValue(initialValue);
    }
  }, [initialValue, internalValue]);

  const { data: initialValueData, isLoading: initialValueLoading } = useOne({
    resource,
    id: initialValueId,
    queryOptions: {
      ...queryOptions,
      enabled: !!initialValueId && (typeof initialValueId === 'string' || typeof initialValueId === 'number'),
    },
  });

  const initialValueOption = useMemo(() => {
    if (!initialValueData?.data) return undefined;
    
    return {
      key: `${initialValueData.data[optionValue]}-initial`,
      label: optionRender(initialValueData.data),
      value: initialValueData.data[optionValue],
      data: initialValueData.data,
    };
  }, [initialValueData?.data, optionValue, optionRender]);

  // Handle initial value loading and value prop changes
  useEffect(() => {
    // Handle initial value loading
    if (
      initialValueOption &&
      isInitialLoad.current &&
      initialValue &&
      initialValueId &&
      !internalValue
    ) {
      isInitialLoad.current = false;
      setInternalValue(initialValueOption.value);
      // Only call onChange if it's provided and the value is different
      if (onChange && initialValueOption.value !== value) {
        onChange(initialValueOption);
      }
    }
    // Handle value prop changes (for controlled component) - but only if not a user selection
    else if (value !== undefined && value !== internalValue && !isUserSelection.current) {
      setInternalValue(value);
    }
  }, [initialValueOption, initialValue, initialValueId, value, internalValue, onChange]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, searchDebounceMs);

    return () => clearTimeout(timer);
  }, [searchValue, searchDebounceMs]);

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  // Filter generation - only apply search filters when there's a search value
  const allFilters = useMemo(() => [
    ...filters,
    ...(debouncedSearchValue && searchFields.length > 0
      ? resource === 'patients'
        ? buildPatientSearchFilters(debouncedSearchValue, searchFields)
        : searchFields.map(field => ({
            field,
            operator: 'contains' as const,
            value: debouncedSearchValue,
          }))
      : []),
  ], [filters, debouncedSearchValue, searchFields, resource]);

  // Data fetching
  const { selectProps, query } = useSelect({
    resource,
    optionValue: (item: any) => item[optionValue],
    filters: allFilters.length > 0 ? allFilters : undefined,
    sorters: sorters.length > 0 ? sorters : undefined,
    pagination,
    queryOptions: {
      ...queryOptions,
    },
  });

  // Loading state - only show loading when query is actually loading
  const showLoading = !!query?.isLoading;

  // Data processing - use the data directly from the API without additional local filtering
  const cachedData = query?.data?.data || [];

  const availableOptions = useMemo(() => {
    return cachedData.map((item: any, index: number) => ({
      key: `${item[optionValue]}-${index}`,
      label: optionRender(item),
      value: item[optionValue],
      data: item, // Keep the original data here
    }));
  }, [cachedData, optionRender, optionValue]);

  const finalOptions = useMemo(() => {
    if (!initialValueOption) {
      return availableOptions;
    }

    const exists = availableOptions.some(
      (opt: any) => opt.value === initialValueOption.value
    );

    if (exists) {
      const filteredOptions = availableOptions.filter(
        (opt: any) => opt.value !== initialValueOption.value
      );
      return [initialValueOption, ...filteredOptions];
    } else {
      return [initialValueOption, ...availableOptions];
    }
  }, [initialValueOption, availableOptions]);

  // Auto-select first option when autoSelectFirst is enabled and no value is set
  useEffect(() => {
    if (
      autoSelectFirst &&
      !internalValue &&
      !initialValue &&
      !value &&
      finalOptions.length > 0 &&
      !query?.isLoading
    ) {
      const firstOption = finalOptions[0];
      if (firstOption) {
        setInternalValue(firstOption.value);
        if (onChange) {
          onChange(firstOption);
        }
      }
    }
  }, [autoSelectFirst, internalValue, initialValue, value, finalOptions, query?.isLoading, onChange]);

  // Event handlers
  const handleChange = useCallback((newValue: any) => {
    if (!onChange) return;
    
    // Mark this as a user selection to prevent value prop interference
    isUserSelection.current = true;
    
    if (!newValue) {
      // Handle clearing the value
      setInternalValue(null);
      onChange(null);
    } else {
      // Handle both labelInValue format and direct value
      const valueToSet = newValue?.value || newValue;
      
      setInternalValue(valueToSet);
      
      // Find the selected option with full data
      const selectedOption = finalOptions.find(option => option.value === valueToSet);
      
      // Pass the full option data if available, otherwise just the value
      onChange(selectedOption || valueToSet);
    }
    
    // Reset the user selection flag after a short delay
    setTimeout(() => {
      isUserSelection.current = false;
    }, 100);
  }, [onChange, finalOptions]);

  // Handle value properly for both edit and create modes
  const selectedValueOption = useMemo(() => {
    // If internalValue is null/undefined, return null (for clearing)
    if (internalValue === null || internalValue === undefined) {
      return null;
    }

    // If we have an initialValueOption and internalValue matches, use it
    if (initialValueOption && initialValueOption.value === internalValue) {
      return initialValueOption;
    }

    // If we have an internalValue but no initialValueOption yet, and we're still loading the initial value
    if (
      internalValue &&
      (typeof internalValue === 'string' || typeof internalValue === 'number') &&
      initialValueLoading
    ) {
      // Return null while loading to prevent showing an empty option
      return null;
    }

    // If we have an internalValue but no initialValueOption, and we're not loading
    if (
      internalValue &&
      (typeof internalValue === 'string' || typeof internalValue === 'number') &&
      !initialValueLoading &&
      !initialValueOption
    ) {
      // This is for edit mode where we have just the ID but couldn't load the data
      // Return null instead of showing "ID: <ID>" to prevent confusion
      return null;
    }

    // Find the matching option from finalOptions for the internalValue
    if (internalValue && finalOptions.length > 0) {
      const matchingOption = finalOptions.find(option => option.value === internalValue);
      if (matchingOption) {
        return matchingOption;
      }
    }

    return internalValue;
  }, [initialValueOption, internalValue, initialValueLoading, finalOptions]);

  return (
    <Select
      className="[&_.ant-select-dropdown]:!min-w-max [&_.ant-select-dropdown]:!max-w-none [&_.ant-select-dropdown_.ant-select-item-option]:!whitespace-normal [&_.ant-select-dropdown_.ant-select-item-option]:!break-words [&_.ant-select-dropdown_.ant-select-item-option]:!overflow-visible"
      filterOption={false}
      allowClear={props.allowClear !== false}
      notFoundContent={showLoading ? <Spin size="small" /> : 'No results found'}
      loading={showLoading}
      status={status}
      {...selectProps}
      {...props}
      onSearch={handleSearch}
      options={finalOptions}
      value={selectedValueOption}
      onChange={handleChange}
      optionRender={option => option.label}
    />
  );
}

export { SearchSelectField };
